// lib/appwrite/bizzApi.ts
import { appwriteConfig, databases, storage } from "@/lib/appwrite/config";
import { notificationService } from "@/services/NotificationService";
import { ID, Query } from "appwrite";

// Types
export interface IVideoPost {
  $id?: string;
  title: string;
  description: string;
  videoUrl: string;
  creatorId: string;
  category: string;
  duration: number;
  isActivated: boolean;
  individual: string; // "bizz"
  createdAt?: string;
  updatedAt?: string;
}

export interface IVideoStats {
  $id?: string;
  videoId: string;
  likes: number;
  shares: number;
  saves: number;
  tips: number;
  views: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface IVideoInteraction {
  $id?: string;
  userId: string;
  videoId: string;
  type: 'like' | 'save' | 'share' | 'view';
  createdAt?: string;
}

export interface IVideoTip {
  $id?: string;
  senderId: string;
  receiverId: string;
  message?: string;
  videoId: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  paymentId?: string;
  orderId?: string;
  createdAt?: string;
}

interface UploadProgress {
  progress: number;
  sizeUploaded: number;
  chunksTotal: number;
  chunksUploaded: number;
}

// ============ VIDEO POST FUNCTIONS ============

export const createVideoPost = async (video: {
  title: string;
  description: string;
  category: string;
  videoUrl: string;
  duration: number;
  creatorId: string;
  individual: string;
  isActivated: boolean;
}) => {
  try {

    // Validate required fields
    if (!video.title?.trim()) {
      throw new Error("Title is required");
    }
    if (!video.videoUrl) {
      throw new Error("Video URL is required");
    }
    if (!video.creatorId) {
      throw new Error("Creator ID is required");
    }

    // Handle duration - convert Infinity, NaN, or invalid values to 0
    let validDuration = 0;
    if (typeof video.duration === 'number' && 
        Number.isFinite(video.duration) && 
        video.duration > 0) {
      validDuration = Math.round(video.duration);
    }


    // Create video document
    const newVideo = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      ID.unique(),
      {
        title: video.title.trim(),
        description: video.description?.trim() || "",
        category: video.category.toLowerCase().trim(),
        videoUrl: video.videoUrl,
        duration: validDuration,
        creatorId: video.creatorId,
        individual: video.individual || "bizz",
        isActivated: video.isActivated !== false,
        field: "", // Empty field as per your collection
        tags: [], // Empty tags array
        language: "en", // Default language
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
    );
    return newVideo;
  } catch (error) {
    console.error("Error creating video post:", error);
    throw new Error(
      error instanceof Error ? error.message : "Failed to create video post"
    );
  }
};

export const uploadVideoFile = async (file: File): Promise<{ videoUrl: string; fileId: string }> => {
  try {

    // Validate file
    if (!file.type.startsWith('video/')) {
      throw new Error('Invalid file type. Please upload a video file.');
    }

    // Generate unique filename
    const fileExtension = file.name.split('.').pop() || 'mp4';
    const fileName = `video_${Date.now()}_${ID.unique()}.${fileExtension}`;

    // Upload to Appwrite Storage
    const uploadedFile = await storage.createFile(
      appwriteConfig.videoBucketId,
      ID.unique(),
      new File([file], fileName, { type: file.type })
    );


    // Get file URL
    const fileUrl = storage.getFileView(
      appwriteConfig.videoBucketId,
      uploadedFile.$id
    );

    return {
      videoUrl: fileUrl.toString(),
      fileId: uploadedFile.$id,
    };
  } catch (error) {
    console.error("Error uploading video file:", error);
    throw new Error(
      error instanceof Error ? error.message : "Failed to upload video file"
    );
  }
};

export const generateThumbnail = async (videoSource: File | string): Promise<string> => {
  try {

    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Cannot get canvas context'));
        return;
      }

      video.onloadeddata = async () => {
        try {
          // Set canvas dimensions (16:9 aspect ratio, reasonable size)
          canvas.width = 320;
          canvas.height = 180;

          // Seek to 1 second or 10% of video duration, whichever is smaller
          const seekTime = Math.min(1, video.duration * 0.1);
          video.currentTime = seekTime;
        } catch (error) {
          reject(error);
        }
      };

      video.onseeked = async () => {
        try {
          // Draw video frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (!blob) {
              reject(new Error('Failed to generate thumbnail blob'));
              return;
            }

            try {
              // Upload thumbnail to storage
              const thumbnailFile = new File([blob], `thumbnail_${Date.now()}.jpg`, {
                type: 'image/jpeg',
              });

              const uploadedThumbnail = await storage.createFile(
                appwriteConfig.videoBucketId,
                ID.unique(),
                thumbnailFile
              );

              const thumbnailUrl = storage.getFileView(
                appwriteConfig.videoBucketId,
                uploadedThumbnail.$id
              );

              // Cleanup
              URL.revokeObjectURL(video.src);
              resolve(thumbnailUrl.toString());
            } catch (uploadError) {
              console.warn('Thumbnail upload failed:', uploadError);
              // Return empty string if thumbnail upload fails - non-critical
              resolve('');
            }
          }, 'image/jpeg', 0.8);
        } catch (error) {
          reject(error);
        }
      };

      video.onerror = () => {
        reject(new Error('Video loading failed'));
      };

      // Set video source
      if (typeof videoSource === 'string') {
        video.src = videoSource;
      } else {
        video.src = URL.createObjectURL(videoSource);
      }

      video.load();
    });
  } catch (error) {
    console.warn("Thumbnail generation failed:", error);
    // Return empty string for non-critical failure
    return '';
  }
};

export async function getVideoFeed(category?: string, lastId?: string) {
  try {
    const queries = [
      Query.equal('individual', 'bizz'),
      Query.equal('isActivated', true),
      Query.orderDesc('$createdAt'),
      Query.limit(10)
    ];

    // Add category filter if provided and not 'all'
    if (category && category !== 'all') {
      queries.push(Query.equal('category', category));
    }

    if (lastId) {
      queries.push(Query.cursorAfter(lastId));
    }

    const videos = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      queries
    );

    // Get stats for each video
    const videosWithStats = await Promise.all(
      videos.documents.map(async (video) => {
        try {
          const stats = await getVideoStats(video.$id);
          const creator = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.userCollectionId,
            video.creatorId
          );

          return {
            ...video,
            creator: {
              $id: creator.$id,
              name: creator.name,
              username: creator.username,
              imageUrl: creator.imageUrl
            },
            stats
          };
        } catch (err) {
          // Skip videos where creator doesn't exist (deleted user)
          console.warn(`Skipping video ${video.$id} - creator not found`);
          return null;
        }
      })
    );

    // Filter out null entries (videos with missing creators)
    const validVideos = videosWithStats.filter((video): video is NonNullable<typeof video> => video !== null);

    return {
      documents: validVideos,
      total: videos.total
    };
  } catch (error) {
    console.error("Error getting video feed:", error);
    throw error;
  }
}

export async function getUserVideos(userId: string) {
  try {
    const videos = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      [
        Query.equal('creatorId', userId),
        Query.equal('individual', 'bizz'),
        Query.orderDesc('$createdAt')
      ]
    );

    return videos;
  } catch (error) {
    console.error("Error getting user videos:", error);
    throw error;
  }
}

export async function deleteVideoPost(videoId: string, userId: string) {
  try {
    // Verify ownership
    const video = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      videoId
    );

    if (video.creatorId !== userId) {
      throw new Error("Not authorized to delete this video");
    }

    // Delete video file from storage
    if (video.fileId) {
      await storage.deleteFile(appwriteConfig.videoBucketId, video.fileId);
    }

    // Delete video document
    await databases.deleteDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      videoId
    );

    // Delete stats
    const stats = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoStatsCollectionId,
      [Query.equal('videoId', videoId)]
    );

    if (stats.documents.length > 0) {
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoStatsCollectionId,
        stats.documents[0].$id
      );
    }

    return { success: true };
  } catch (error) {
    console.error("Error deleting video:", error);
    throw error;
  }
}

// ============ VIDEO INTERACTION FUNCTIONS ============

export async function likeVideoPost(videoId: string, userId: string) {
  try {
    // Check if already liked
    const existingLike = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.equal('userId', userId),
        Query.equal('type', 'like')
      ]
    );

    if (existingLike.documents.length > 0) {
      // Unlike - delete the interaction
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoInteractionsCollectionId,
        existingLike.documents[0].$id
      );

      // Decrease like count
      await updateVideoStats(videoId, 'likes', -1);
      
      return { isLiked: false };
    } else {
      // Like - create interaction
      await databases.createDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoInteractionsCollectionId,
        ID.unique(),
        {
          videoId,
          userId,
          type: 'like',
          createdAt: new Date().toISOString(),
        }
      );

      // Increase like count
      await updateVideoStats(videoId, 'likes', 1);

      // Create notification for video creator
      try {
        const video = await databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.videosCollectionId,
          videoId
        );

        if (video.creatorId !== userId) {
          const liker = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.userCollectionId,
            userId
          );

          await notificationService.createNotification({
            title: "Video Liked",
            body: `${liker.name} liked your video: ${video.title}`,
            type: "video_like",
            recipient_id: video.creatorId,
            sender_id: userId
          });
        }
      } catch (notificationError) {
        console.warn("Failed to create like notification:", notificationError);
      }

      return { isLiked: true };
    }
  } catch (error) {
    console.error("Error liking video:", error);
    throw error;
  }
}

export async function saveVideoPost(videoId: string, userId: string) {
  try {
    // Check if already saved
    const existingSave = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.equal('userId', userId),
        Query.equal('type', 'save')
      ]
    );

    if (existingSave.documents.length > 0) {
      // Unsave
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoInteractionsCollectionId,
        existingSave.documents[0].$id
      );

      await updateVideoStats(videoId, 'saves', -1);
      return { isSaved: false };
    } else {
      // Save
      await databases.createDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoInteractionsCollectionId,
        ID.unique(),
        {
          videoId,
          userId,
          type: 'save',
          createdAt: new Date().toISOString(),
        }
      );

      await updateVideoStats(videoId, 'saves', 1);
      return { isSaved: true };
    }
  } catch (error) {
    console.error("Error saving video:", error);
    throw error;
  }
}

export async function shareVideoPost(videoId: string, userId: string) {
  try {
    // Create share interaction
    await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      ID.unique(),
      {
        videoId,
        userId,
        type: 'share',
        createdAt: new Date().toISOString(),
      }
    );

    // Increase share count
    await updateVideoStats(videoId, 'shares', 1);

    return { success: true };
  } catch (error) {
    console.error("Error sharing video:", error);
    throw error;
  }
}

export async function viewVideoPost(_videoId: string, _userId: string) {
  try {
    // ✅ SKIP view tracking entirely for now to stop the spam
    return { success: true };
    
    /* COMMENTED OUT UNTIL FIXED:
    // Check if already viewed in the last hour
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    
    const recentView = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.equal('userId', userId),
        Query.equal('type', 'view'),
        Query.greaterThan('createdAt', oneHourAgo)
      ]
    );

    if (recentView.documents.length === 0) {
      await databases.createDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoInteractionsCollectionId,
        ID.unique(),
        {
          videoId,
          userId,
          type: 'view',
          createdAt: new Date().toISOString(),
        }
      );
      await updateVideoStats(videoId, 'views', 1);
    }
    */

    return { success: true };
  } catch (error) {
    console.error("Error recording video view:", error);
    return { success: false };
  }
}

// ============ VIDEO STATS FUNCTIONS ============

export async function getVideoStats(videoId: string) {
  try {
    const stats = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoStatsCollectionId,
      [Query.equal('videoId', videoId)]
    );

    if (stats.documents.length === 0) {
      // Create default stats if not exists
      const defaultStats = await databases.createDocument(
        appwriteConfig.databaseId,
        appwriteConfig.videoStatsCollectionId,
        ID.unique(),
        {
          videoId,
          likes: 0,
          shares: 0,
          saves: 0,
          tips: 0,
          views: 0,
          createdAt: new Date().toISOString(),
        }
      );
      return defaultStats;
    }

    return stats.documents[0];
  } catch (error) {
    console.error("Error getting video stats:", error);
    throw error;
  }
}

export async function updateVideoStats(
  videoId: string, 
  field: 'likes' | 'shares' | 'saves' | 'tips' | 'views', 
  increment: number
) {
  try {
    const stats = await getVideoStats(videoId);
    const currentValue = stats[field] || 0;
    const newValue = Math.max(0, currentValue + increment);

    await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videoStatsCollectionId,
      stats.$id,
      {
        [field]: newValue,
        updatedAt: new Date().toISOString(),
      }
    );

    return newValue;
  } catch (error) {
    console.error("Error updating video stats:", error);
    throw error;
  }
}

export async function getUserVideoInteractions(userId: string, videoIds: string[]) {
  try {
    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('userId', userId),
        Query.contains('videoId', videoIds)
      ]
    );

    // Group by video and type
    const interactionMap: Record<string, Record<string, boolean>> = {};
    
    interactions.documents.forEach((interaction) => {
      if (!interactionMap[interaction.videoId]) {
        interactionMap[interaction.videoId] = {};
      }
      interactionMap[interaction.videoId][interaction.type] = true;
    });

    return interactionMap;
  } catch (error) {
    console.error("Error getting user interactions:", error);
    throw error;
  }
}

// ============ VIDEO TIP FUNCTIONS ============

export async function createVideoTip(tip: Omit<IVideoTip, '$id' | 'createdAt'>) {
  try {
    const newTip = await databases.createDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId,
      ID.unique(),
      {
        ...tip,
        createdAt: new Date().toISOString(),
      }
    );

    return newTip;
  } catch (error) {
    console.error("Error creating video tip:", error);
    throw error;
  }
}

export async function updateVideoTipStatus(
  tipId: string, 
  status: 'completed' | 'failed' | 'pending', 
  paymentId?: string
) {
  try {
    const updatedTip = await databases.updateDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId,
      tipId,
      {
        status,
        paymentId,
        updatedAt: new Date().toISOString(),
      }
    );

    // If completed, update tip count
    if (status === 'completed') {
      await updateVideoStats(updatedTip.videoId, 'tips', 1);

      // Create notification for tip receiver
      try {
        const sender = await databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.userCollectionId,
          updatedTip.senderId
        );

        const video = await databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.videosCollectionId,
          updatedTip.videoId
        );

        await notificationService.createNotification({
          title: "Tip Received",
          body: `${sender.name} sent you ₹${updatedTip.amount} tip for your video: ${video.title}`,
          type: "video_tip",
          recipient_id: updatedTip.receiverId,
          sender_id: updatedTip.senderId
        });
      } catch (notificationError) {
        console.warn("Failed to create tip notification:", notificationError);
      }
    }

    return updatedTip;
  } catch (error) {
    console.error("Error updating tip status:", error);
    throw error;
  }
}

export async function getUserVideoTips(userId: string, type: 'sent' | 'received') {
  try {
    const field = type === 'sent' ? 'senderId' : 'receiverId';
    
    const tips = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId,
      [
        Query.equal(field, userId),
        Query.equal('status', 'completed'),
        Query.orderDesc('$createdAt')
      ]
    );

    return tips;
  } catch (error) {
    console.error("Error getting user video tips:", error);
    throw error;
  }
}

export const getVideoTips = async (videoId: string) => {
  try {
    const response = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId, // Replace with your actual collection ID
      [
        Query.equal('videoId', videoId),
        Query.equal('status', 'completed')
      ]
    );

    // Calculate total amount and count
    const total = response.documents.reduce((sum, tip) => sum + tip.amount, 0);
    const count = response.documents.length;

    return { total, count };
  } catch (error) {
    console.error('Error fetching video tips:', error);
    return { total: 0, count: 0 };
  }
};

// ============ SEARCH AND DISCOVERY ============

export async function searchVideos(query: string, category?: string) {
  try {
    const queries = [
      Query.equal('individual', 'bizz'),
      Query.equal('isActivated', true),
      Query.search('title', query),
      Query.limit(20)
    ];

    if (category && category !== 'all') {
      queries.push(Query.equal('category', category));
    }

    const videos = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      queries
    );

    return videos;
  } catch (error) {
    console.error("Error searching videos:", error);
    throw error;
  }
}

export async function getTrendingVideos() {
  try {
    // Get videos with highest engagement in the last 7 days
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    
    const stats = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoStatsCollectionId,
      [
        Query.greaterThan('createdAt', sevenDaysAgo),
        Query.orderDesc('likes'),
        Query.limit(20)
      ]
    );

    // Get corresponding videos
    const videoIds = stats.documents.map(stat => stat.videoId);
    
    if (videoIds.length === 0) return { documents: [] };

    const videos = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      [
        Query.contains('$id', videoIds),
        Query.equal('individual', 'bizz'),
        Query.equal('isActivated', true)
      ]
    );

    return videos;
  } catch (error) {
    console.error("Error getting trending videos:", error);
    throw error;
  }
}

export async function getVideoAnalytics(videoId: string, timeframe: '7d' | '30d' | '90d' = '7d') {
  try {
    const days = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    
    // Get interactions within timeframe
    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.greaterThan('createdAt', startDate),
        Query.orderDesc('createdAt')
      ]
    );
    
    // Get tips within timeframe
    const tips = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.equal('status', 'completed'),
        Query.greaterThan('createdAt', startDate)
      ]
    );
    
    // Calculate metrics
    const analytics = {
      totalViews: interactions.documents.filter(i => i.type === 'view').length,
      totalLikes: interactions.documents.filter(i => i.type === 'like').length,
      totalShares: interactions.documents.filter(i => i.type === 'share').length,
      totalSaves: interactions.documents.filter(i => i.type === 'save').length,
      totalTips: tips.documents.length,
      totalTipAmount: tips.documents.reduce((sum, tip) => sum + tip.amount, 0),
      engagementRate: 0, // Calculate based on views vs interactions
      dailyBreakdown: {}, // Group by day
      topInteractionHours: {}, // Most active hours
    };
    
    // Calculate engagement rate
    if (analytics.totalViews > 0) {
      const totalEngagements = analytics.totalLikes + analytics.totalShares + analytics.totalSaves;
      analytics.engagementRate = (totalEngagements / analytics.totalViews) * 100;
    }
    
    return analytics;
    
  } catch (error) {
    console.error("Error getting video analytics:", error);
    throw error;
  }
}

export async function getUserAnalytics(userId: string, timeframe: '7d' | '30d' | '90d' = '30d') {
  try {
    const days = timeframe === '7d' ? 7 : timeframe === '30d' ? 30 : 90;
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
    
    // Get user's videos
    const userVideos = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      [
        Query.equal('creatorId', userId),
        Query.greaterThan('createdAt', startDate)
      ]
    );
    
    // Get tips received
    const tipsReceived = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoTipsCollectionId,
      [
        Query.equal('receiverId', userId),
        Query.equal('status', 'completed'),
        Query.greaterThan('createdAt', startDate)
      ]
    );
    
    const analytics = {
      totalVideos: userVideos.documents.length,
      totalTipsReceived: tipsReceived.documents.length,
      totalTipAmount: tipsReceived.documents.reduce((sum, tip) => sum + tip.amount, 0),
      averageTipAmount: 0,
      topPerformingVideo: null,
      totalViews: 0,
      totalLikes: 0,
      totalShares: 0,
      totalSaves: 0,
    };
    
    if (analytics.totalTipsReceived > 0) {
      analytics.averageTipAmount = analytics.totalTipAmount / analytics.totalTipsReceived;
    }
    
    return analytics;
    
  } catch (error) {
    console.error("Error getting user analytics:", error);
    throw error;
  }
}

export const validateVideoFile = (file: File): { isValid: boolean; error?: string } => {
  // Check file type
  if (!file.type.startsWith('video/')) {
    return { isValid: false, error: 'Invalid file type. Please select a video file.' };
  }

  // Check supported formats
  const supportedFormats = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/avi',
    'video/mov',
    'video/quicktime',
    'video/x-msvideo'
  ];

  if (!supportedFormats.includes(file.type)) {
    return { isValid: false, error: 'Unsupported video format. Please use MP4, WebM, or MOV.' };
  }

  return { isValid: true };
};

// Get video metadata function
export const getVideoMetadata = (file: File): Promise<{
  duration: number;
  width: number;
  height: number;
  size: number;
}> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    
    video.onloadedmetadata = () => {
      resolve({
        duration: Math.floor(video.duration),
        width: video.videoWidth,
        height: video.videoHeight,
        size: file.size,
      });
      URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
      URL.revokeObjectURL(video.src);
    };

    video.src = URL.createObjectURL(file);
  });
};

export const cleanupFailedUpload = async (fileId: string): Promise<void> => {
  try {
    if (fileId) {
      await storage.deleteFile(
        appwriteConfig.videoBucketId,
        fileId
      );
    }
  } catch (error) {
    console.warn("Failed to cleanup file:", error);
    // Non-critical error, don't throw
  }
};

// Utility functions for video duration calculation
export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    
    video.onloadedmetadata = () => {
      const duration = Math.floor(video.duration);
      URL.revokeObjectURL(video.src);
      resolve(duration);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
      URL.revokeObjectURL(video.src);
    };

    video.src = URL.createObjectURL(file);
  });
};

// Enhanced upload function (kept for compatibility but can use existing uploadFile)
export const uploadVideoFileOptimized = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<{ videoUrl: string; fileId: string }> => {
  try {
    
    // Generate unique file ID before upload (important for chunked uploads)
    const fileId = ID.unique();
    
    // Calculate expected chunks (5MB each)
    const chunkSize = 5 * 1024 * 1024; // 5MB
    const expectedChunks = Math.ceil(file.size / chunkSize);
    
    
    // Start upload with progress callback
    const result = await storage.createFile(
      appwriteConfig.videoBucketId,
      fileId,
      file,
      [], // permissions - adjust as needed
      (progress) => {
        // Progress is reported per chunk (every 5MB)
        const progressData: UploadProgress = {
          progress: progress.progress || 0,
          sizeUploaded: progress.sizeUploaded || 0,
          chunksTotal: progress.chunksTotal || expectedChunks,
          chunksUploaded: progress.chunksUploaded || 0
        };
        
        
        // Call the progress callback if provided
        if (onProgress) {
          onProgress(progressData);
        }
      }
    );

    
    // Return the file URL and ID
    const videoUrl = storage.getFileView(
      appwriteConfig.videoBucketId,
      result.$id
    );
    
    return {
      videoUrl: videoUrl.toString(),
      fileId: result.$id
    };
    
  } catch (error) {
    console.error('Upload failed:', error);
    
    // Handle specific Appwrite errors
    if (error instanceof Error) {
      if (error.message.includes('rate_limit_exceeded')) {
        throw new Error('Upload rate limit exceeded. Please wait a moment and try again.');
      }
      if (error.message.includes('file_size_exceeded')) {
        throw new Error('File size exceeds the maximum allowed limit.');
      }
      if (error.message.includes('insufficient_permissions')) {
        throw new Error('Insufficient permissions to upload file.');
      }
    }
    
    throw new Error('Failed to upload video file. Please try again.');
  }
};

export async function getUserLikedVideos(userId: string, pageParam?: string) {
  try {
    let queries = [
      Query.equal('userId', userId),
      Query.equal('type', 'like'),
      Query.orderDesc('createdAt'),
      Query.limit(20)
    ];

    if (pageParam) {
      queries.push(Query.cursorAfter(pageParam));
    }

    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      queries
    );

    // Get video details for each interaction
    const videos = await Promise.all(
      interactions.documents.map(async (interaction) => {
        try {
          const video = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.videosCollectionId,
            interaction.videoId
          );

          // Get creator info
          const creator = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.userCollectionId,
            video.creatorId
          );

          return {
            ...video,
            creator,
            interactionDate: interaction.createdAt
          };
        } catch (error) {
          console.warn(`Video ${interaction.videoId} not found:`, error);
          return null;
        }
      })
    );

    return {
      documents: videos.filter(video => video !== null),
      total: interactions.total
    };
  } catch (error) {
    console.error("Error fetching user liked videos:", error);
    throw error;
  }
}

export async function getUserSavedVideos(userId: string, pageParam?: string) {
  try {
    let queries = [
      Query.equal('userId', userId),
      Query.equal('type', 'save'),
      Query.orderDesc('createdAt'),
      Query.limit(20)
    ];

    if (pageParam) {
      queries.push(Query.cursorAfter(pageParam));
    }

    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      queries
    );

    // Get video details for each interaction
    const videos = await Promise.all(
      interactions.documents.map(async (interaction) => {
        try {
          const video = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.videosCollectionId,
            interaction.videoId
          );

          // Get creator info
          const creator = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.userCollectionId,
            video.creatorId
          );

          return {
            ...video,
            creator,
            interactionDate: interaction.createdAt
          };
        } catch (error) {
          console.warn(`Video ${interaction.videoId} not found:`, error);
          return null;
        }
      })
    );

    return {
      documents: videos.filter(video => video !== null),
      total: interactions.total
    };
  } catch (error) {
    console.error("Error fetching user saved videos:", error);
    throw error;
  }
}

export async function getSingleVideo(videoId: string) {
  try {
    // Get video data
    const video = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.videosCollectionId,
      videoId
    );

    // Get creator info
    const creator = await databases.getDocument(
      appwriteConfig.databaseId,
      appwriteConfig.userCollectionId,
      video.creatorId
    );

    // Get video stats
    const statsQueries = [
      Query.equal('videoId', videoId),
      Query.select(['type'])
    ];

    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      statsQueries
    );

    // Calculate stats
    const stats = {
      likes: interactions.documents.filter(doc => doc.type === 'like').length,
      saves: interactions.documents.filter(doc => doc.type === 'save').length,
      shares: interactions.documents.filter(doc => doc.type === 'share').length,
      tips: interactions.documents.filter(doc => doc.type === 'tip').length,
      views: interactions.documents.filter(doc => doc.type === 'view').length,
    };

    return {
      ...video,
      creator,
      stats
    };
  } catch (error) {
    console.error("Error fetching single video:", error);
    throw error;
  }
}

export async function getVideoInteractions(videoId: string, userId: string) {
  try {
    const interactions = await databases.listDocuments(
      appwriteConfig.databaseId,
      appwriteConfig.videoInteractionsCollectionId,
      [
        Query.equal('videoId', videoId),
        Query.equal('userId', userId),
        Query.select(['type', '$id'])
      ]
    );

    // FIX: Proper type definitions
    const userInteractions: {
      isLiked: boolean;
      isSaved: boolean;
      likeId: string | null;
      saveId: string | null;
    } = {
      isLiked: false,
      isSaved: false,
      likeId: null,
      saveId: null,
    };

    interactions.documents.forEach(interaction => {
      if (interaction.type === 'like') {
        userInteractions.isLiked = true;
        userInteractions.likeId = interaction.$id;
      } else if (interaction.type === 'save') {
        userInteractions.isSaved = true;
        userInteractions.saveId = interaction.$id;
      }
    });

    return userInteractions;
  } catch (error) {
    console.error("Error fetching video interactions:", error);
    throw error;
  }
}

